import { EnvConfig } from '@config';
import { DueDatesDTO } from 'pages/WellGuardian/WIR/wir.constants';
import queryString from 'query-string';
import { ApiResponse } from 'types/apiResponse';
import { UserDTO } from 'types/user';
import {
  Alert,
  AlertsCount,
  AnnuliGauge,
  AnnulusStatus,
  AssetType,
  ChecklistQuestions,
  CheckScheduleCalculateDTO,
  CheckScheduleResponseDTO,
  CommentEditWIR,
  CommentWIR,
  Derogation,
  DgsHse,
  EstimatedGain,
  Integrity,
  IntegrityStatus,
  Intervention,
  KeyLinkD,
  LatestWellPotentials,
  PauseCalculateDTO,
  PausePersistenceDTO,
  ProductionRate,
  R2SLink,
  riskStatus,
  ScheduleWIR,
  SiteLayout,
  SiteType,
  SubscriptionDTO,
  SubscriptionType,
  SynergiCase,
  TargetInfo,
  WellBehaviour,
  WellBehaviourActivity,
  WellBehaviourClusterStats,
  WellInfo,
  WellResources,
  WellReviews,
  WellReviewsFetch,
  WellSchematic,
  WellSchematicOther,
  WHTemperaturePressure,
  WIR,
  WIRDTO,
  WIREquipmentIdentification,
  WirInfoFetch,
  WIRInitialRequestInformation,
  WIRListPresentation,
  WIRManagerialSummary,
  WIROrganiseKickOffMeeting,
  WIRPhaseTypes,
  WIRSChecksActionsSectionDTO,
  WIRSectionDTO,
  WIRSignOffDTO,
  WIRUser,
  WIRWellInformation,
} from 'types/wellGuardian';

import Client from './client';

const wellGuardianApiBaseUrl = EnvConfig.drillx_wg_api_url || '';
const accept = 'application/json';

class WellGuardianApiClient {
  client: Client;

  constructor() {
    this.client = new Client(wellGuardianApiBaseUrl, accept);
  }

  getAzureBlobFile = (
    filename: string,
    schematicType: string,
    tuwi: string,
    country: string,
  ): Promise<Blob> => {
    return this.client.getBlob(
      `/azure-blob-storage/schematics/${tuwi}/${country}/${encodeURIComponent(
        filename,
      )}/${encodeURIComponent(schematicType)}`,
    );
  };

  getWellSchematicByTuwi = async (
    tuwi: string,
    country: string,
  ): Promise<ApiResponse<WellSchematic[]>> => {
    try {
      const response = await this.client.get(`/well-schematic/${tuwi}/${country}`);

      return { response };
    } catch (error) {
      return { error, response: [] };
    }
  };

  getWellSchematicByTuwiOther = async (
    tuwi: string,
    country: string,
  ): Promise<ApiResponse<WellSchematicOther[]>> => {
    try {
      const response = await this.client.get(`/well-schematic/${tuwi}/${country}`);

      return { response };
    } catch (error) {
      return { error, response: [] };
    }
  };

  getWellInfoByTuwi = async (
    tuwi: string,
    country: string,
  ): Promise<ApiResponse<WellInfo | null>> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/well-info/${country}`);

      return { response };
    } catch (error) {
      return { error, response: null };
    }
  };

  getResourcesByTuwi = async (tuwi: string): Promise<WellResources[]> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/resources`);

      return response;
    } catch (error) {
      return [];
    }
  };

  createResources = async (
    tuwi: string,
    createResourceDto: Partial<WellResources>,
  ): Promise<WellResources | null> => {
    try {
      const createResources = await this.client.post(`/well/${tuwi}/resources`, createResourceDto);

      return createResources;
    } catch (error) {
      return null;
    }
  };

  deleteResources = async (tuwi: string, id: string | undefined): Promise<boolean> => {
    try {
      await this.client.delete(`/well/${tuwi}/resources/${id}`);

      return true;
    } catch (error) {
      return false;
    }
  };

  getReviewsByTuwi = async (tuwi: string): Promise<WellReviewsFetch[]> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/well-reviews`);

      return response;
    } catch (error) {
      return [];
    }
  };

  createReviews = async (
    tuwi: string,
    createResourceDto: Partial<WellReviews>,
  ): Promise<WellReviews | null> => {
    try {
      const createResources = await this.client.post(
        `/well/${tuwi}/well-reviews`,
        createResourceDto,
      );

      return createResources;
    } catch (error) {
      return null;
    }
  };

  editReviews = async (
    tuwi: string,
    id: string,
    createResourceDto: Partial<WellReviews>,
  ): Promise<WellReviews | null> => {
    try {
      const createResources = await this.client.patch(
        `/well/${tuwi}/well-reviews/${id}`,
        createResourceDto,
      );

      return createResources;
    } catch (error) {
      return null;
    }
  };

  deleteReviews = async (tuwi: string, id: string | undefined): Promise<boolean> => {
    try {
      await this.client.delete(`/well/${tuwi}/well-reviews/${id}`);

      return true;
    } catch (error) {
      return false;
    }
  };

  getLinksByCountry = async (country: string): Promise<KeyLinkD[]> => {
    try {
      const response = await this.client.get(`/well/${country}/keylinks`);

      return response;
    } catch (error) {
      return [];
    }
  };

  createLinks = async (
    country: string,
    createLinkDto: Partial<KeyLinkD>,
  ): Promise<KeyLinkD | null> => {
    try {
      const createLinks = await this.client.post(`/well/${country}/keylinks`, createLinkDto);

      return createLinks;
    } catch (error) {
      return null;
    }
  };

  editLinks = async (
    country: string,
    id: string,
    editLinkDto: Partial<KeyLinkD>,
  ): Promise<KeyLinkD | null> => {
    try {
      const editLinks = await this.client.patch(`/well/${country}/keylinks/${id}`, editLinkDto);

      return editLinks;
    } catch (error) {
      return null;
    }
  };

  deleteLinks = async (country: string, id: string | undefined): Promise<boolean> => {
    try {
      await this.client.delete(`/well/${country}/keylinks/${id}`);

      return true;
    } catch (error) {
      return false;
    }
  };

  getToolTips = async (country: string): Promise<WirInfoFetch[]> => {
    try {
      const response = await this.client.get(`/wir/tools-tips/${country}`);

      return response;
    } catch (error) {
      return [];
    }
  };

  createToolTips = async (
    country: string,
    createToolTips: Partial<WirInfoFetch>,
  ): Promise<WirInfoFetch | null> => {
    try {
      const createTips = await this.client.post(`/wir/tools-tips/${country}`, createToolTips);

      return createTips;
    } catch (error) {
      return null;
    }
  };

  editToolTips = async (
    country: string,
    id: string,
    editToolTips: Partial<WirInfoFetch>,
  ): Promise<WirInfoFetch | null> => {
    try {
      const editTips = await this.client.patch(`/wir/tools-tips/${country}/${id}`, editToolTips);

      return editTips;
    } catch (error) {
      return null;
    }
  };

  deleteToolTips = async (country: string, id: string | undefined): Promise<boolean> => {
    try {
      await this.client.delete(`/wir/tools-tips/${country}/${id}`);

      return true;
    } catch (error) {
      return false;
    }
  };

  getIntegrityStatusByTuwi = async (tuwi: string): Promise<IntegrityStatus[]> => {
    try {
      const data = await this.client.get(`/well/${tuwi}/integrity-status`);

      return data;
    } catch (error) {
      return [];
    }
  };

  getAnnulusStatusByTuwi = async (tuwi: string): Promise<AnnulusStatus[]> => {
    try {
      const data = await this.client.get(`/well/${tuwi}/annulus-status`);

      return data;
    } catch (error) {
      return [];
    }
  };

  getAnnuliGaugesByTuwi = async (tuwi: string, country: string): Promise<AnnuliGauge[]> => {
    try {
      const data = await this.client.get(`/well/${country}/${tuwi}/annuli-gauges`);

      return data;
    } catch (error) {
      return [];
    }
  };

  getLatestWellPotentialByTuwi = async (
    tuwi: string,
    country: string,
  ): Promise<LatestWellPotentials[]> => {
    try {
      const data = await this.client.get(`/well/${tuwi}/latest-well-potential/${country}`);

      return data;
    } catch (error) {
      return [];
    }
  };

  getProductionRateByTuwiAndByDate = async (
    tuwi: string,
    dateFrom: string,
    dateTo: string,
  ): Promise<ProductionRate[]> => {
    try {
      const data = await this.client.get(
        `/well/${tuwi}/production-rates?datefrom=${dateFrom}&dateto=${dateTo}`,
      );

      return data;
    } catch (error) {
      return [];
    }
  };

  getWHTemperaturePressureByTuwiAndByDate = async (
    tuwi: string,
    dateFrom: string,
    dateTo: string,
  ): Promise<WHTemperaturePressure[]> => {
    try {
      const data = await this.client.get(
        `/well/${tuwi}/temp-pressure?datefrom=${dateFrom}&dateto=${dateTo}`,
      );

      return data;
    } catch (error) {
      return [];
    }
  };

  getDerogations = async (
    siteName: SiteType,
    country: string,
    assetName: string,
  ): Promise<Derogation[]> => {
    try {
      const data = await this.client.get(
        `/derogations/${assetName}/${siteName}/${country}?activeonly=true`,
      );

      return data;
    } catch (error) {
      return [];
    }
  };

  getInterventionsByTuwi = async (tuwi: string, country: string): Promise<Intervention[]> => {
    try {
      const data = await this.client.get(`/well/${tuwi}/${country}/interventions`);

      return data;
    } catch (error) {
      return [];
    }
  };

  getEstimatedGainByTuwi = async (tuwi: string): Promise<Intervention[]> => {
    try {
      const data = await this.client.get(`/well/${tuwi}/interventions/gains`);

      return data;
    } catch (error) {
      return [];
    }
  };

  createEstimatedGain = async (
    country: string,
    gainPayload: Partial<EstimatedGain>,
  ): Promise<EstimatedGain | null> => {
    try {
      const createEstimatedGain = await this.client.post(
        `/well/${country}/interventions/create`,
        gainPayload,
      );

      return createEstimatedGain;
    } catch (error) {
      return null;
    }
  };

  updateEstimatedGain = async (
    country: string,
    gainPayload: Partial<EstimatedGain>,
  ): Promise<EstimatedGain | null> => {
    try {
      const updateEstimatedGain = await this.client.patch(
        `/well/${country}/interventions/update`,
        gainPayload,
      );

      return updateEstimatedGain;
    } catch (error) {
      return null;
    }
  };

  getSynergiCasesTuwi = async (
    asset: AssetType,
    site: SiteType,
    country: string,
    tuwi: string,
  ): Promise<DgsHse[]> => {
    try {
      const data = await this.client.get(`/synergi-cases/${country}/${asset}/${site}?tuwi=${tuwi}`);

      return data;
    } catch (error) {
      return [];
    }
  };

  getIntegrityTuwi = async (tuwi: string): Promise<Integrity[]> => {
    try {
      const data = await this.client.get(`/well/${tuwi}/well-integrity`);

      return data;
    } catch (error) {
      return [];
    }
  };

  getSynergiCases = async (
    asset: AssetType,
    site: SiteType,
    country: string,
  ): Promise<SynergiCase[]> => {
    try {
      const data = await this.client.get(`/synergi-cases/${country}/${asset}/${site}`);

      return data;
    } catch (error) {
      return [];
    }
  };

  getAlertsCountByTuwi = async (tuwi: string): Promise<ApiResponse<AlertsCount | null>> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/alerts-count`);

      return { response };
    } catch (error) {
      return { error, response: null };
    }
  };

  getWellsInfoForSite = async (
    assetId: string,
    siteId: string,
    countrySelect: string,
  ): Promise<WellInfo[]> => {
    try {
      const data = await this.client.get(`/well/wells-info/${assetId}/${siteId}/${countrySelect}`);

      return data;
    } catch (error) {
      return [];
    }
  };

  getTargetViewData = async (
    asset: string,
    site: string,
    countrySelect: string,
  ): Promise<TargetInfo[]> => {
    try {
      const data = await this.client.get(
        `/well/latest-well-potential/${asset}/${site}/${countrySelect}`,
      );

      return data;
    } catch (error) {
      return [];
    }
  };

  getSiteLayoutsById = async (siteId: string): Promise<SiteLayout | null> => {
    try {
      const data = await this.client.get(`/well/site-layouts/${siteId}`);

      return data;
    } catch (error) {
      return null;
    }
  };

  getAlertsByTuwi = async (tuwi: string): Promise<ApiResponse<Alert[] | null>> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/alerts`);

      return { response };
    } catch (error) {
      return { error, response: null };
    }
  };

  getWellBehaviourByTuwiAndByDate = async (
    tuwi: string,
    datefrom: string,
    dateto: string,
    country: string,
  ): Promise<ApiResponse<WellBehaviour[]>> => {
    try {
      const response = await this.client.get(
        `/well/${tuwi}/${country}/well-behaviour?datefrom=${datefrom}&dateto=${dateto}`,
      );

      return { response };
    } catch (error) {
      return { error, response: [] };
    }
  };

  getWellBehaviourClustersByTuwi = async (
    tuwi: string,
    country: string,
  ): Promise<ApiResponse<WellBehaviourClusterStats[]>> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/${country}/well-behaviour/clusters`);

      return { response };
    } catch (error) {
      return { error, response: [] };
    }
  };

  getSubscriptions = async (): Promise<SubscriptionDTO | null> => {
    try {
      const data = await this.client.get(`/well/my-alert-subscriptions`);

      return data;
    } catch (error) {
      return null;
    }
  };

  createSubscription = async (
    subscription: Partial<SubscriptionDTO>,
  ): Promise<SubscriptionDTO | null> => {
    try {
      const createdSubscription = await this.client.post('/well/alert-subscription', subscription);

      return createdSubscription;
    } catch (error) {
      return null;
    }
  };

  getAllPossibleSubscriptions = async (): Promise<SubscriptionType[]> => {
    try {
      const data = await this.client.get(`/well/possible-subscriptions`);

      return data;
    } catch (error) {
      return [];
    }
  };

  getActiveAlertsByTuwis = async (tuwis: string[]): Promise<Alert[]> => {
    try {
      const query = queryString.stringify({ tuwis }, { arrayFormat: 'bracket' });

      return await this.client.get(`/wells/active-alerts-for-tuwis?${query}`);
    } catch (error) {
      return [];
    }
  };

  processAlerts = async (alertIds: string[]): Promise<Alert[] | null> => {
    try {
      const archivedAlerts = await this.client.patch('/well/archive-alerts', {
        alertIds,
      });

      return archivedAlerts;
    } catch (error) {
      return null;
    }
  };

  unProcessAlerts = async (alertIds: string[]): Promise<Alert[] | null> => {
    try {
      const unArchivedAlerts = await this.client.patch('/well/un-archive-alerts', { alertIds });

      return unArchivedAlerts;
    } catch (error) {
      return null;
    }
  };

  getAllAlertsByTuwis = async (tuwis: string[]): Promise<Alert[]> => {
    try {
      const query = queryString.stringify({ tuwis }, { arrayFormat: 'bracket' });

      return await this.client.get(`/wells/all-alerts-for-tuwis?${query}`);
    } catch (error) {
      return [];
    }
  };

  getWellBehaviourActivityLogByTuwi = async (
    tuwi: string,
  ): Promise<ApiResponse<WellBehaviourActivity[]>> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/well-behaviour/activity-log`);

      return { response };
    } catch (error) {
      return { error, response: [] };
    }
  };

  createWellBehaviourActivity = async (
    tuwi: string,
    activityEntry: Partial<WellBehaviourActivity>,
  ): Promise<WellBehaviourActivity | null> => {
    try {
      const createdActivity = await this.client.post(
        `/well/${tuwi}/well-behaviour/activity`,
        activityEntry,
      );

      return createdActivity;
    } catch (error) {
      return null;
    }
  };

  deleteWellBehaviourActivity = async (tuwi: string, id: string): Promise<boolean> => {
    try {
      await this.client.delete(`/well/${tuwi}/well-behaviour/activity/${id}`);

      return true;
    } catch (error) {
      return false;
    }
  };

  getR2SLink = async (id: string, linkType: string): Promise<R2SLink | null> => {
    try {
      const r2SLink = await this.client.get(`/well/r2s-link?id=${id}&linkType=${linkType}`);

      return r2SLink;
    } catch (error) {
      return null;
    }
  };

  getAllWir = async (countryState: string): Promise<WIRListPresentation[]> => {
    try {
      const wirList = await this.client.get(`/wir/all/${countryState}`);

      return wirList;
    } catch (error) {
      return [];
    }
  };

  getWirById = async (wirId: string): Promise<WIR | null> => {
    try {
      const wir = await this.client.get(`/wir/${wirId}`);

      return wir;
    } catch (error) {
      return null;
    }
  };

  createWIR = async (wirCreateDto: WIRDTO, country: string): Promise<WIR | null> => {
    try {
      const createdWir = await this.client.post(`/wir/${country}`, wirCreateDto);

      return createdWir;
    } catch (error) {
      return null;
    }
  };

  updateWIRInitialRequestInformation = async (
    wirId: string,
    initialRequestDto: WIRInitialRequestInformation,
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(
        `/wir/${wirId}/phase/${phase}/section/${country}/INITIAL_REQUEST_INFORMATION`,
        initialRequestDto,
      );
    } catch (error) {
      return null;
    }
  };

  updateWIROrganiseKickOffMeeting = async (
    wirId: string,
    organiseKickOffMeetingDto: WIROrganiseKickOffMeeting,
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(
        `/wir/${wirId}/phase/${phase}/section/${country}/ORGANISE_KICK_OFF_MEETING`,
        organiseKickOffMeetingDto,
      );
    } catch (error) {
      return null;
    }
  };

  updateWIREquipmentIdentification = async (
    wirId: string,
    equipmentIdentificationDto: WIREquipmentIdentification,
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(
        `/wir/${wirId}/phase/${phase}/section/${country}/EQUIPMENT_IDENTIFICATION`,
        equipmentIdentificationDto,
      );
    } catch (error) {
      return null;
    }
  };

  updateWIRGenericSection = async (
    wirId: string,
    sectionGenericDto: WIRSectionDTO,
    country: string,
  ): Promise<WIR | null> => {
    try {
      const updatedWir = await this.client.post(
        `/wir/${wirId}/section/${country}`,
        sectionGenericDto,
      );

      return updatedWir;
    } catch (error) {
      return null;
    }
  };

  updateWIRWellInformationSection = async (
    wirId: string,
    wellInformationSectionDto: WIRWellInformation,
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(
        `/wir/${wirId}/phase/${phase}/section/${country}/WELL_INFORMATION`,
        wellInformationSectionDto,
      );
    } catch (error) {
      return null;
    }
  };

  updateWIRCloseoutChecklistSection = async (
    wirId: string,
    wellInformationSectionDto: WIRSectionDTO[],
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post1(
        `/wir/${wirId}/close-out/section/${country}?phase=${phase} `,
        wellInformationSectionDto,
      );
    } catch (error) {
      return null;
    }
  };

  updateWIRChecksActions = async (
    wirId: string,
    sectionDTO: WIRSChecksActionsSectionDTO,
  ): Promise<WIR | null> => {
    try {
      const updatedWir = await this.client.post(`/wir/${wirId}/checklist-section`, sectionDTO);

      return updatedWir;
    } catch (error) {
      return null;
    }
  };

  wirUsersList = async (roleId: string, country?: string): Promise<WIRUser[]> => {
    try {
      const usersList = await this.client.get(
        `/wir/user-config/role-members/${roleId}?country=${country}`,
      );

      return usersList;
    } catch (error) {
      return [];
    }
  };

  addSignOff = async (wirId: string, signOffDTO: WIRSignOffDTO): Promise<WIR | null> => {
    try {
      const updatedWir = await this.client.post(`/wir/${wirId}/approvals`, signOffDTO);

      return updatedWir;
    } catch (error) {
      return null;
    }
  };

  getChecklistQs = async (country: string): Promise<ChecklistQuestions | null> => {
    try {
      const response = await this.client.get(`/wir-config/checklists/${country}`);

      return response;
    } catch (error) {
      return null;
    }
  };

  pauseWir = async (
    wirId: string,
    pausePersistenceDTO: PausePersistenceDTO,
  ): Promise<WIR | null> => {
    try {
      const updatedWir = await this.client.post(`/wir/${wirId}/pause`, pausePersistenceDTO);

      return updatedWir;
    } catch (error) {
      return null;
    }
  };

  calculatePauseWir = async (
    wirId: string,
    pausePersistenceDTO: PauseCalculateDTO,
  ): Promise<CheckScheduleCalculateDTO | null> => {
    try {
      const updatedWir = await this.client.post(
        `/wir/${wirId}/pause/calculate`,
        pausePersistenceDTO,
      );

      return updatedWir;
    } catch (error) {
      return null;
    }
  };

  updateWIRManagerialSummarySection = async (
    wirId: string,
    managerialSummarySectionDto: WIRManagerialSummary,
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(
        `/wir/${wirId}/phase/${phase}/section/${country}/MANAGERIAL_SUMMARY`,
        managerialSummarySectionDto,
      );
    } catch (error) {
      return null;
    }
  };

  getWellLogFile = (tuwi: string): Promise<Blob> => {
    return this.client.getBlob(`/azure-blob-storage/well-logs/${tuwi}`);
  };

  checkWirDueDates = async (
    interventionDate: string,
    interventionComplexity: string,
    expectedEnggTime: number,
  ): Promise<CheckScheduleResponseDTO | null> => {
    try {
      const data = await this.client.get(
        `/wir/check-due-dates?interventionDate=${interventionDate}&interventionComplexity=${interventionComplexity}&expectedEnggTime=${expectedEnggTime}`,
      );

      return data;
    } catch (error) {
      return null;
    }
  };

  createWIRUser = async (userCreateDto: WIRUser, country: string): Promise<WIRUser | null> => {
    try {
      return await this.client.post(`/wir/user-config/${country}`, userCreateDto);
    } catch (error) {
      return null;
    }
  };

  editWIRUser = async (userEditDto: WIRUser, country: string): Promise<WIRUser | null> => {
    try {
      return await this.client.patch(`/wir/user-config/user/${country}`, userEditDto);
    } catch (error) {
      return null;
    }
  };

  deleteWIRUser = async (userId: string): Promise<boolean> => {
    try {
      await this.client.delete(`/wir/user-config/user/${userId}`);

      return true;
    } catch (error) {
      return false;
    }
  };

  getScheduleWirs = async (country: string): Promise<ScheduleWIR[]> => {
    try {
      return await this.client.get(`/wir/schedule-view/${country}?asset=ALL`);
    } catch (error) {
      return [];
    }
  };

  getCommentWirs = async (wirId: string | undefined): Promise<CommentWIR[]> => {
    try {
      const response = await this.client.get(`/wir/${wirId}/comments`);

      return response;
    } catch (error) {
      return [];
    }
  };

  createCommentWIR = async (
    wirID: string | undefined,
    wirCommentDto: CommentWIR,
  ): Promise<CommentWIR | null> => {
    try {
      const createCommentWIR = await this.client.post(`/wir/${wirID}/comments`, wirCommentDto);

      return createCommentWIR;
    } catch (error) {
      return null;
    }
  };

  editCommentWIR = async (
    wirID: string | undefined,
    wirCommentDto: CommentEditWIR,
    id: string | undefined,
  ): Promise<CommentEditWIR | null> => {
    try {
      const createCommentWIR = await this.client.patch(
        `/wir/${wirID}/comments/${id}`,
        wirCommentDto,
      );

      return createCommentWIR;
    } catch (error) {
      return null;
    }
  };

  getPendingTasks = async (country: string, wirId?: string | null): Promise<riskStatus[]> => {
    try {
      if (wirId === null) {
        const response = await this.client.get(`/wir/pending/${country}`);

        return response;
      } else {
        const response = await this.client.get(`/wir/pending/${country}?wirId=${wirId}`);

        return response;
      }
    } catch (error) {
      return [];
    }
  };

  deleteCommentWIR = async (
    wirId: string | undefined,
    id: string | undefined,
  ): Promise<boolean> => {
    try {
      await this.client.delete(`/wir/${wirId}/comments/${id}`);

      return true;
    } catch (error) {
      return false;
    }
  };

  unPauseInterventionOperationWIR = async (wirId: string, country: string): Promise<WIR | null> => {
    try {
      const updatedWir = await this.client.post(`/wir/${wirId}/unpause_interv_op/${country}`, {});

      return updatedWir;
    } catch (error) {
      return null;
    }
  };

  deleteArchivedWIR = async (wirId: string | undefined, country: string): Promise<boolean> => {
    try {
      await this.client.delete(`/wir/delete/${wirId}/${country}`);

      return true;
    } catch (error) {
      return false;
    }
  };

  createWGUser = async (user: Partial<UserDTO>): Promise<UserDTO | null> => {
    try {
      const createdWGUser = await this.client.post('/users/drillx', user);

      return createdWGUser;
    } catch (error) {
      return null;
    }
  };

  deleteWGUser = async (igg: string): Promise<boolean> => {
    try {
      await this.client.delete(`/users/drillx/${igg}`);

      return true;
    } catch (error) {
      return false;
    }
  };

  createWGDueDate = async (wirId: string, dueDateRequest: DueDatesDTO[]): Promise<WIR | null> => {
    try {
      return await this.client.post1(`/wir/date-change/${wirId}`, dueDateRequest);
    } catch (error) {
      return null;
    }
  };
}

export default new WellGuardianApiClient();
